<template>
  <Layout>
    <div class="program-header mb-4">
      <b-breadcrumb>
        <b-breadcrumb-item>
          <b-icon
            class="me-2"
            icon="house-fill"
            scale="1.25"
            shift-v="1.25"
            aria-hidden="true"
          ></b-icon>
          Home
        </b-breadcrumb-item>
      </b-breadcrumb>
    </div>

    <!-- Error Message -->
    <b-alert v-if="errorMessage" show variant="danger" class="text-center">
      <h4 class="alert-heading">Akses Terbatas</h4>
      <p>Anda tidak mempunyai akses pada halaman ini</p>
      <hr />
      <a href="/logout" class="btn btn-outline-danger">
        <i class="uil uil-sign-out-alt me-2"></i>
        {{ $t("navbar.dropdown.marcus.list.logout") }}
      </a>
    </b-alert>

    <!-- Program Grid -->
    <div v-if="!isLoading" class="program-grid">
      <b-row>
        <!-- Dynamic Programs -->
        <b-col
          v-for="program in setProgrammes"
          :key="program.id"
          lg="4"
          md="6"
          sm="12"
          class="mb-4"
        >
          <router-link
            :to="
              isProgrammeAccessible(program)
                ? `/program/${program.name.toLowerCase()}/${program.id}`
                : '#'
            "
            :class="[
              'text-decoration-none',
              { 'disabled-link': !isProgrammeAccessible(program) },
            ]"
          >
            <div
              class="program-card h-100"
              :class="{ 'programme-disabled': !isProgrammeAccessible(program) }"
            >
              <div class="d-flex align-items-center gap-2">
                <div
                  class="program-icon"
                  :class="{ 'disabled-icon': !isProgrammeAccessible(program) }"
                >
                  <b-icon icon="journal-text" scale="2"></b-icon>
                </div>
                <h3 class="program-title">{{ program.name }}</h3>
              </div>

              <div class="program-badges">
                <div
                  v-if="program.sub_programmes.length === 0"
                  class="empty-badge"
                >
                  Tidak ada sub program
                </div>
                <div v-else class="badge-container">
                  <!-- Perbaikan: Menggunakan method untuk filter daripada v-if dengan v-for -->
                  <span
                    v-for="(sub, index) in getAccessibleSubProgrammes(program)"
                    :key="index"
                    class="sub-badge"
                  >
                    {{ sub.name }}
                  </span>
                </div>
              </div>

              <div class="program-footer">
                <span class="view-details">
                  {{
                    isProgrammeAccessible(program)
                      ? "Lihat Detail"
                      : "Tidak Dapat Diakses"
                  }}
                  <b-icon icon="arrow-right" class="ms-2"></b-icon>
                </span>
              </div>
            </div>
          </router-link>
        </b-col>

        <!-- Static Practice Program -->
        <b-col lg="4" md="6" sm="12" class="mb-4">
          <router-link to="/program/latihan/Aq08" class="text-decoration-none">
            <div class="program-card h-100">
              <div class="d-flex align-items-center gap-2">
                <div class="program-icon practice">
                  <b-icon icon="pencil-square" scale="2"></b-icon>
                </div>
                <h3 class="program-title">LATIHAN</h3>
              </div>

              <div class="program-badges">
                <div class="empty-badge">
                  Tidak ada sub program
                </div>
              </div>

              <div class="program-footer">
                <span class="view-details">
                  Lihat Detail
                  <b-icon icon="arrow-right" class="ms-2"></b-icon>
                </span>
              </div>
            </div>
          </router-link>
        </b-col>
      </b-row>
    </div>

    <!-- Loading State -->
    <div v-else class="loading-container">
      <loading-vue></loading-vue>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import axios from "axios";
import loadingVue from "@/components/loading.vue";
import { authHeader } from "../../../helpers/fakebackend/auth-header";

export default {
  components: {
    Layout,
    loadingVue,
  },
  page: {
    title: "Student",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      title: "CBT - Siswa",
      items: [
        { text: "Dashboard", href: "/" },
        { text: "Student", active: true },
      ],
      setProgrammes: [],
      isLoading: true,
      errorMessage: false,
      userData: null,
      userProgrammes: [],
    };
  },
  async created() {
    // Diubah untuk menyelesaikan masalah timing
    try {
      await this.getMe(); // Tunggu hingga data user selesai
      await this.getDataProgrammes(); // Kemudian ambil data program
    } catch (error) {
      console.error("Error initializing data:", error);
      this.errorMessage = true;
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async getMe() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/me`,
          { headers: authHeader() }
        );
        const userData = response.data.data;
        this.userData = userData;
        this.userProgrammes = userData.programmes || [];
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Tangani error jika gagal mengambil data
        this.errorMessage = true;
      }
    },
    async getDataProgrammes() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_API_BASE_URL + "/api/programmes"
        );
        this.setProgrammes = response.data.data;
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.errorMessage = true;
        }
        console.error("Error fetching programmes:", error);
      }
    },
    isProgrammeAccessible(program) {
      // Cek apakah programme dapat diakses berdasarkan daftar programme pengguna
      if (!this.userProgrammes || this.userProgrammes.length === 0)
        return false;

      return this.userProgrammes.some(
        (userProgramme) => userProgramme.id === program.id
      );
    },
    // Method baru untuk memfilter sub-programmes yang dapat diakses
    getAccessibleSubProgrammes(program) {
      if (!program.sub_programmes) return [];
      return program.sub_programmes.filter((sub) =>
        this.isProgrammeAccessible(sub)
      );
    },
  },
  middleware: "authentication",
};
</script>

<style scoped>
.program-header h2 {
  color: #2d3748;
  font-weight: 600;
}

.program-grid {
  margin-top: 1rem;
}

.program-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.program-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.program-icon {
  background: #e6d5fd;
  color: #8b21f1;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.program-icon.practice {
  background: #fef3c7;
  color: #d97706;
}

.program-title {
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.badge-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.sub-badge {
  background: #4ade80;
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.empty-badge {
  background: #9ca3af;
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-block;
}

.program-footer {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.view-details {
  color: #8b21f1;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-container {
  height: 74vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.disabled-link {
  pointer-events: disabled;
  opacity: 0.6;
}

.programme-disabled {
  background-color: #f0f0f0;
  cursor: not-allowed;
}

.disabled-icon {
  opacity: 0.5;
}
</style>
